/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

// EULA acceptance persistence, decoupled from React so the auth state
// machine can resolve it imperatively (with an explicit timeout) instead
// of through a hook with its own effect lifecycle.

import { get, ref, set, type Database } from "firebase/database";

// Per-user localStorage key. A device-global key would let User A's
// acceptance silently apply to User B on the same device.
const eulaKey = (uid: string) => `eulaAccepted:${uid}`;

// Older builds wrote a global "eulaAccepted" key. Remove it once so it
// can't leak across accounts.
const LEGACY_KEY = "eulaAccepted";

export function clearLegacyEula(): void {
  try {
    localStorage.removeItem(LEGACY_KEY);
  } catch {
    /* localStorage unavailable — non-fatal */
  }
}

export function readLocalEula(uid: string): string | null {
  try {
    return localStorage.getItem(eulaKey(uid));
  } catch {
    return null;
  }
}

export function writeLocalEula(uid: string, timestamp: string): void {
  try {
    localStorage.setItem(eulaKey(uid), timestamp);
  } catch {
    /* non-fatal */
  }
}

export function clearLocalEula(uid: string): void {
  try {
    localStorage.removeItem(eulaKey(uid));
  } catch {
    /* non-fatal */
  }
}

export async function readRemoteEula(
  database: Database,
  uid: string,
): Promise<string | null> {
  const snap = await get(ref(database, `users/${uid}/eulaAccepted`));
  return snap.exists() ? (snap.val() as string) : null;
}

export async function writeRemoteEula(
  database: Database,
  uid: string,
  timestamp: string,
): Promise<void> {
  await set(ref(database, `users/${uid}/eulaAccepted`), timestamp);
}
